import { useState, useEffect } from 'react';
import { Plus, Briefcase, TrendingUp, Percent, RefreshCw, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import AddInvestmentAccountModal from '../components/AddInvestmentAccountModal';
import AddHoldingModal from '../components/AddHoldingModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Investment {
  id: string;
  symbol: string;
  name: string;
  quantity: number;
  cost_basis: number;
  current_price: number;
  asset_class: string;
}

interface Account {
  id: string;
  name: string;
  institution: string;
  account_type: string;
  balance: number;
}

export default function Investments() {
  const { profile } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showHoldingModal, setShowHoldingModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setIsLoading(true);
      setError(null);
      const { data: accountsData, error: accountsError } = await supabase
        .from('investment_accounts')
        .select('*')
        .order('created_at', { ascending: false });
      if (accountsError) throw accountsError;

      const { data: investmentsData, error: investmentsError } = await supabase
        .from('investment_holdings')
        .select('*')
        .order('created_at', { ascending: false });
      if (investmentsError) throw investmentsError;

      setAccounts(accountsData || []);
      setInvestments(investmentsData || []);
    } catch {
      setError('Failed to load investment data');
    } finally {
      setIsLoading(false);
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: profile?.currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const calculateTotalValue = () => investments.reduce((total, inv) => total + (inv.quantity * inv.current_price), 0);
  const calculateTotalGainLoss = () =>
    investments.reduce((total, inv) => {
      const costBasis = inv.quantity * inv.cost_basis;
      const currentValue = inv.quantity * inv.current_price;
      return total + (currentValue - costBasis);
    }, 0);
  const calculatePerformance = () => {
    const totalCost = investments.reduce((total, inv) => total + (inv.quantity * inv.cost_basis), 0);
    const totalValue = calculateTotalValue();
    return totalCost === 0 ? 0 : ((totalValue - totalCost) / totalCost) * 100;
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 animate-spin rounded-full border-2 border-slate-900 border-t-transparent mx-auto" />
          <p className="mt-4 text-slate-600">Loading investment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Investments</h1>
          <p className="text-sm text-slate-600">Track your investment portfolio</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift">
              <Plus className="w-5 h-5" />
              Add Investment
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setShowAccountModal(true)}>Add Account</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowHoldingModal(true)}>Add Holding</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AddInvestmentAccountModal open={showAccountModal} onClose={() => setShowAccountModal(false)} onSuccess={fetchData} />
      <AddHoldingModal open={showHoldingModal} onClose={() => setShowHoldingModal(false)} onSuccess={fetchData} />

      {error && (
        <Alert variant="destructive" className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-slate-900">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-5 h-5 text-slate-700" />
              <h3 className="text-sm font-medium text-slate-600">Total Value</h3>
            </div>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotalValue())}</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-600">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-medium text-slate-600">Total Gain/Loss</h3>
            </div>
            <p className={`text-2xl font-bold ${calculateTotalGainLoss() >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
              {formatCurrency(calculateTotalGainLoss())}
            </p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="w-5 h-5 text-slate-600" />
              <h3 className="text-sm font-medium text-slate-600">Performance</h3>
            </div>
            <p className={`text-2xl font-bold ${calculatePerformance() >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
              {calculatePerformance().toFixed(2)}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900">Investment Accounts</h2>
          <Button variant="ghost" size="sm" className="hover:bg-slate-100 text-slate-700" onClick={() => setShowAccountModal(true)}>Add Account</Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accounts.map((account) => (
              <Card key={account.id}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-medium text-slate-900">{account.name}</h3>
                      <p className="text-sm text-slate-600">{account.institution}</p>
                    </div>
                    <span className="text-xs text-slate-500 capitalize">{account.account_type}</span>
                  </div>
                  <p className="text-lg font-semibold text-slate-900">{formatCurrency(account.balance)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <h2 className="text-lg font-semibold text-slate-900">Holdings</h2>
          <Button variant="ghost" size="icon" className="hover:bg-slate-100" onClick={() => fetchData()} title="Refresh data">
            <RefreshCw className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Symbol</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Cost Basis</TableHead>
                <TableHead>Current Price</TableHead>
                <TableHead>Market Value</TableHead>
                <TableHead>Gain/Loss</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {investments.map((investment) => {
                const marketValue = investment.quantity * investment.current_price;
                const costBasis = investment.quantity * investment.cost_basis;
                const gainLoss = marketValue - costBasis;
                const gainLossPercent = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0;
                return (
                  <TableRow key={investment.id}>
                    <TableCell className="font-medium text-slate-900">{investment.symbol}</TableCell>
                    <TableCell className="text-slate-600">{investment.name}</TableCell>
                    <TableCell className="text-slate-900">{investment.quantity}</TableCell>
                    <TableCell className="text-slate-600">{formatCurrency(investment.cost_basis)}</TableCell>
                    <TableCell className="text-slate-600">{formatCurrency(investment.current_price)}</TableCell>
                    <TableCell className="font-medium text-slate-900">{formatCurrency(marketValue)}</TableCell>
                    <TableCell>
                      <div className={gainLoss >= 0 ? 'text-slate-700' : 'text-red-600'}>
                        <div className="font-medium">{formatCurrency(gainLoss)}</div>
                        <div className="text-sm">{gainLossPercent.toFixed(2)}%</div>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
